/**
 * Near-exact match between a query and a page's own name, tolerant of typos.
 *
 * WHY (measured 2026-08-10): coverage's typo credit is binary above a 0.6
 * trigram floor, and common one-letter slips land just under it — so queries
 * for pages that EXIST were declining and offering to create a duplicate:
 * "iphne 17 pro max" had iphone-17-pro-max-en-CA ranked #1 by score at
 * coverage 0.67 (gate 0.70), "airpds pro" had airpods-pro-en-CA at 0.33,
 * "wireles keybord" had wireless-keyboards-en-CA at 0.50. Declining a typo'd
 * exact-product query is the page-recreation vector get-supabase-search.ts
 * documents (it is how the iPhone 17 Pro Max page got duplicated once).
 *
 * This helper answers ONE narrow question: "is the query the page's own name,
 * up to small typos?" — every query token must map onto a subject token within
 * a length-scaled edit distance, digits must match exactly, and the subject
 * may carry at most 2 unmatched tokens. It RESCUES display (the hit is shown
 * on the shelf) and never auto-opens: redirect confidence stays owned by
 * canAutoRedirect on real coverage.
 */

const MAX_TOKENS = 12;

/** Levenshtein distance with an early-out cap. */
const editDistance = (a: string, b: string, max: number): number => {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  if (a === b) return 0;
  const prev = new Array<number>(b.length + 1);
  const curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1;
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
};

const tokenize = (text: string): string[] =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
    .slice(0, MAX_TOKENS);

const isDigits = (token: string): boolean => /^\d+$/.test(token);

/** Allowed typo budget by token length: exact ≤2 chars, 1 edit to 5, 2 above. */
const allowedEdits = (length: number): number => (length <= 2 ? 0 : length <= 5 ? 1 : 2);

export const isNearExactSubjectMatch = (
  query: string | null | undefined,
  subject: string | null | undefined,
): boolean => {
  if (!query || !subject) return false;
  const queryTokens = tokenize(query);
  const subjectTokens = tokenize(subject);
  if (queryTokens.length === 0 || subjectTokens.length === 0) return false;
  // A subject saying much more than was asked is a different page, not a typo.
  if (subjectTokens.length > queryTokens.length + 2) return false;

  const used = new Array<boolean>(subjectTokens.length).fill(false);
  for (const queryToken of queryTokens) {
    let matched = false;
    for (let i = 0; i < subjectTokens.length; i++) {
      if (used[i]) continue;
      const subjectToken = subjectTokens[i];
      if (isDigits(queryToken) || isDigits(subjectToken)) {
        if (queryToken !== subjectToken) continue;
      } else {
        // A slip keeps the word's first letter; a different first letter is a
        // different word. Without this, the length-scaled budget (2 edits at 6+
        // chars) reads "mens" and "womens" as the same token — measured
        // 2026-08-29, it let women's pages rescue past the coverage floor for a
        // men's query and count as "this page already exists" against creating
        // the men's one. Every rescue this function was built for keeps its
        // first letter: iphne/iphone, airpds/airpods, wireles/wireless,
        // keybord/keyboard, macha/matcha.
        if (queryToken[0] !== subjectToken[0]) continue;
        const budget = allowedEdits(Math.max(queryToken.length, subjectToken.length));
        if (editDistance(queryToken, subjectToken, budget) > budget) continue;
      }
      used[i] = true;
      matched = true;
      break;
    }
    if (!matched) return false;
  }
  return true;
};

/** True when some query token of length ≥4 is a 1–2-edit typo of a title token.
 *  Used so a lexicon-rescued `macha` → matcha hit is not coverage-vetoed, and
 *  so long SEO titles can still be typo-rescued (isNearExactSubjectMatch
 *  rejects subjects with more than 2 extra tokens). */
export const titleContainsTypoOfQuery = (
  query: string | null | undefined,
  title: string | null | undefined,
): boolean => {
  if (!query || !title) return false;
  const queryTokens = tokenize(query).filter((t) => t.length >= 4 && !isDigits(t));
  const titleTokens = tokenize(title).filter((t) => !isDigits(t));
  if (queryTokens.length === 0 || titleTokens.length === 0) return false;
  return queryTokens.every((queryToken) => {
    const budget = allowedEdits(queryToken.length);
    return titleTokens.some((titleToken) => editDistance(queryToken, titleToken, budget) <= budget);
  });
};
